import { Component, ErrorInfo, ReactNode } from "react";
import { logError } from "@/lib/sentry";
import { Button } from "@/components/ui/button";
import { isPageImportError, pageUpdateAvailable } from "@/lib/app-release";

interface ErrorFallbackProps {
  updateAvailable: boolean;
  checkingUpdate: boolean;
  reload: () => void;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (props: ErrorFallbackProps) => ReactNode;
  errorLabel?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  pageImportFailure: boolean;
  checkingUpdate: boolean;
  updateAvailable: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, pageImportFailure: false, checkingUpdate: false, updateAvailable: false };
  private mounted = true;

  static getDerivedStateFromError(error: unknown) {
    const pageImportFailure = isPageImportError(error);
    return { hasError: true, pageImportFailure, checkingUpdate: pageImportFailure, updateAvailable: false };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    logError(this.props.errorLabel ?? "UI render error", error, info);
    if (isPageImportError(error)) {
      void pageUpdateAvailable(error).then(updateAvailable => {
        if (this.mounted) this.setState({ updateAvailable, checkingUpdate: false });
      });
    }
  }

  componentDidMount() {
    this.mounted = true;
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback({
        updateAvailable: this.state.updateAvailable,
        checkingUpdate: this.state.checkingUpdate,
        reload: this.handleReload,
      });
      return (
        <div role="alert" className="min-h-screen flex items-center justify-center bg-background text-foreground">
          <div className="max-w-md text-center space-y-4">
            <h1 className="text-2xl font-semibold">{this.state.updateAvailable
              ? "AudioTrack was updated"
              : this.state.pageImportFailure ? "This page could not load" : "Something went wrong"}</h1>
            <p className="text-sm text-muted-foreground">
              {this.state.updateAvailable
                ? "Reload to continue with the latest version."
                : this.state.checkingUpdate
                  ? "Checking whether a newer version is available."
                  : this.state.pageImportFailure
                    ? "Check your connection and reload to try again."
                    : "The page hit an unexpected error. Reload to try again."}
            </p>
            <Button onClick={this.handleReload} variant="primary">
              Reload page
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
